// src/components/EnhancedNetworkGraph.jsx
import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

/**
 * Enhanced network graph visualization using only d3
 * Styled to match your existing UI design with gradients and animations
 */
const EnhancedNetworkGraph = ({ data, width = 800, height = 500 }) => {
  const svgRef = useRef(null);
  
  useEffect(() => {
    if (!data || !data.nodes || !data.links || data.nodes.length === 0) {
      return;
    }
    
    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove();
    
    // Create the SVG container
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);
    
    // Add zoom functionality
    const g = svg.append('g');
    svg.call(d3.zoom()
      .extent([[0, 0], [width, height]])
      .scaleExtent([0.25, 5])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      }));
      
    // Define gradients
    const defs = svg.append('defs');
    
    // Gradient for root node
    const rootGradient = defs.append('linearGradient')
      .attr('id', 'rootGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
      
    rootGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#6366f1');
      
    rootGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#8b5cf6');
      
    // Gradient for direct subordinates
    const level1Gradient = defs.append('linearGradient')
      .attr('id', 'level1Gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
      
    level1Gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#10b981');
      
    level1Gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#34d399');
      
    // Gradient for indirect subordinates
    const level2Gradient = defs.append('linearGradient')
      .attr('id', 'level2Gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
      
    level2Gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#3b82f6');
      
    level2Gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#60a5fa');
    
    // Create forces
    const simulation = d3.forceSimulation(data.nodes)
      .force('link', d3.forceLink(data.links).id(d => d.id).distance(120))
      .force('charge', d3.forceManyBody().strength(-500))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(50));
    
    // Create links with styling and animation
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(data.links)
      .join('line')
      .attr('stroke', d => {
        const source = typeof d.source === 'object' ? d.source : data.nodes.find(n => n.id === d.source);
        if (source && source.isRoot) {
          return 'rgba(99, 102, 241, 0.4)'; // Primary color for links from root
        } else {
          return 'rgba(156, 163, 175, 0.3)'; // Secondary color for other links
        }
      })
      .attr('stroke-width', d => Math.sqrt(d.value || 1) * 1.5)
      .attr('stroke-dasharray', '4,4')
      .attr('opacity', 0.7);
    
    // Create node groups with styling
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(data.nodes)
      .join('g')
      .attr('class', d => `node ${d.isRoot ? 'root' : d.directSubordinate ? 'direct' : 'indirect'}`)
      .call(drag(simulation));
    
    // Add node circles with gradient fills
    node.append('circle')
      .attr('r', d => {
        // Size based on node importance
        if (d.isRoot) return 18;
        if (d.directSubordinate) return 14;
        return 10;
      })
      .attr('fill', d => {
        // Fill based on node type using gradients
        if (d.isRoot) return 'url(#rootGradient)';
        if (d.directSubordinate) return 'url(#level1Gradient)';
        return 'url(#level2Gradient)';
      })
      .attr('stroke', '#111827')
      .attr('stroke-width', 2);
    
    // Add pulse animation for important nodes
    node.filter(d => d.isRoot)
      .append('circle')
      .attr('r', 18)
      .attr('fill', 'none')
      .attr('stroke', 'rgba(99, 102, 241, 0.4)')
      .attr('stroke-width', 2)
      .attr('class', 'pulse-circle');
    
    // Add node labels with background
    node.append('g')
      .attr('class', 'label-container')
      .attr('transform', d => `translate(0, ${d.isRoot ? 32 : 24})`)
      .each(function(d) {
        // Add background rect for text
        const bg = d3.select(this)
          .append('rect')
          .attr('rx', 4)
          .attr('ry', 4)
          .attr('fill', 'rgba(17, 24, 39, 0.75)')
          .attr('stroke', d.isRoot ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.1)')
          .attr('stroke-width', 1)
          .attr('width', 100)  // Initial width, will be updated
          .attr('height', 22)
          .attr('x', -50)
          .attr('y', -11);
        
        // Add text
        const text = d3.select(this)
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('fill', '#fff')
          .attr('font-size', '11px')
          .text(d.name);
        
        // Adjust background rect width to fit text
        const bbox = text.node().getBBox();
        bg.attr('width', bbox.width + 16)
          .attr('x', -bbox.width / 2 - 8);
      });
    
    // Add tooltips
    node.append('title')
      .text(d => `${d.name}\nTier: ${d.tier}`);
    
    // Update node and link positions on each tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      
      // Keep nodes within bounds
      node.attr('transform', d => {
        d.x = Math.max(30, Math.min(width - 30, d.x));
        d.y = Math.max(30, Math.min(height - 30, d.y));
        return `translate(${d.x},${d.y})`;
      });
    });
    
    // Drag function for nodes
    function drag(simulation) {
      function dragstarted(event) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }
      
      function dragged(event) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }
      
      function dragended(event) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }
      
      return d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended);
    }
    
    // Position root node in center initially
    const rootNode = data.nodes.find(node => node.isRoot);
    if (rootNode) {
      rootNode.fx = width / 2;
      rootNode.fy = height / 2;
      
      // Release the fixed position after initial centering
      setTimeout(() => {
        rootNode.fx = null;
        rootNode.fy = null;
      }, 1500);
    }
    
    // Add subtle constant movement to simulation for more dynamic appearance
    d3.interval(() => {
      simulation.alpha(0.1).restart();
    }, 3000);
    
    // Clean up simulation when component unmounts
    return () => {
      simulation.stop();
    };
  }, [data, width, height]);
  
  return (
    <div className="enhanced-network-graph">
      <svg ref={svgRef} />
      <div className="graph-legend">
        <div className="legend-item">
          <span className="legend-color root"></span>
          <span className="legend-label">Your Node</span>
        </div>
        <div className="legend-item">
          <span className="legend-color direct"></span>
          <span className="legend-label">Direct Subordinates</span>
        </div>
        <div className="legend-item">
          <span className="legend-color indirect"></span>
          <span className="legend-label">Indirect Subordinates</span>
        </div>
      </div>
    </div>
  );
};

export default EnhancedNetworkGraph;
